import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FirGiltiConsolidatedGridService } from './fir-gilti-consolidated-grid.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-gilti-consolidated-grid',
    templateUrl: './fir-gilti-consolidated-grid.component.html',
    styleUrls: ['./fir-gilti-consolidated-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirGiltiConsolidatedGridComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subscriptions = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  colData: any = {};
  gridData: any = {};

  modalTitle = 'GILTI Consolidated Grid';
  crudLoading = false;
  isSaveClicked = false;

  ho_leid = '';
  ho_cdr_no = '';
  ho_combination_key = '';

  @Input('col-data')
  set _colData(colData: string) {
    try {
      this.colData = safeJsonInput<any>(colData, null, 'fir-gilti-consolidated-grid.parse1');
      this.applyWorkspaceContext();
      this.tryInit();
    } catch (e) {
      console.log('Col data input not yet stable.');
    }
  }

  @Input('grid-data')
  set _gridData(gridData: string) {
    try {
      this.gridData = safeJsonInput<any>(gridData, null, 'fir-gilti-consolidated-grid.parse2');
      this.applyWorkspaceContext();
      this.tryInit();
    } catch (e) {
      console.log('Grid data input not yet stable.');
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private giltiConsolidatedGridService: FirGiltiConsolidatedGridService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = this.baseURLs;
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = this.normalizeRowPayload(rawRow);
    const activeData = activeScreen?.data || {};
    const sel = activeData?.selected;
    const gridFromScreen = gridPayloadFromActiveScreen(activeScreen);
    this.gridData =
      sel != null
        ? sel
        : gridFromScreen != null && typeof gridFromScreen === 'object'
          ? gridFromScreen
          : this.gridData;
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      modalScreen: mergedGp?.modalScreen,
      filterParams: fp,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
  }

  /** Unwrap row payload shapes from workspace / grid selection (parity with bridge `row-data`). */
  private normalizeRowPayload(rawRow: any): any {
    if (rawRow == null) {
      return {};
    }
    if (Array.isArray(rawRow)) {
      if (!rawRow.length) {
        return {};
      }
      const first = rawRow[0];
      const inner = first?.data !== undefined ? first.data : first;
      return extractObject(inner);
    }
    const ext = extractObject(rawRow);
    if (ext.data && typeof ext.data === 'object' && ext.HO_LEID == null && ext.TAX_YEAR == null) {
      return extractObject(ext.data);
    }
    return ext;
  }

  private tryInit(): void {
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.init();
  }

  private init() {
    try {
      this.ho_leid = this.rowData.HO_LEID;
      this.ho_cdr_no = this.rowData.HO_CDR_NO;
      this.ho_combination_key = this.rowData.HO_COMBINATION_KEY;
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in init:', e);
    }
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const colId = this.colData?.MAP || this.colData?.map || '';

    this.giltiConsolidatedGridService.saveManualData(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      colId,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Changes have been saved', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMsg = response.errorMessage && response.errorMessage !== 'null'
            ? response.errorMessage
            : 'An error has occurred. Please try again.';
          this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
